import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";

interface BoardMobileLayoutProps {
  children: React.ReactNode;
}

/**
 * Mobile layout for the board view: stacks lists as full-width,
 * scroll-snapped panels that the user can swipe between. Each panel owns its
 * own vertical scroll so long lists can still be scrolled through without
 * pushing neighbors off screen.
 *
 * Desktop retains the multi-column layout used elsewhere — this component is
 * only rendered when the viewport is narrow.
 */
const BoardMobileLayout: React.FC<BoardMobileLayoutProps> = ({ children }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const childArray = useMemo(() => Children.toArray(children), [children]);
  const childCount = childArray.length;

  // When the set of panels shrinks (e.g. a filter hides lists), the
  // previously-active index may be out of range. Clamp it so the pagination
  // indicator stays in sync with what's actually rendered.
  useEffect(() => {
    setActiveIndex((prev) => {
      if (childCount === 0) return 0;
      return prev >= childCount ? childCount - 1 : prev;
    });
  }, [childCount]);

  // Measure how much vertical space we can give the swipe area. We want each
  // panel to fill the remaining viewport below the header/filter row so the
  // page itself doesn't scroll — only the active panel's content does.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const updateHeight = () => {
      const top = scroller.getBoundingClientRect().top;
      // Leave room for the pagination dots (roughly 28px) plus a little breathing room.
      const next = Math.max(280, window.innerHeight - top - 36);
      setAvailableHeight(next);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
    };
  }, []);

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, clientWidth } = scroller;
    if (clientWidth === 0) return;
    const idx = Math.round(scrollLeft / clientWidth);
    setActiveIndex((prev) => (prev === idx ? prev : idx));
  }, []);

  const scrollToIndex = useCallback((idx: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    // Smooth scrolling is motion the user can ask not to see, and `behavior`
    // passed explicitly overrides the CSS `scroll-behavior` rule, so the
    // preference has to be honoured here rather than in the stylesheet.
    scroller.scrollTo({
      left: idx * scroller.clientWidth,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, []);

  return (
    <div className="-mx-4">
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide"
        style={{
          height: availableHeight ?? undefined,
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
        }}
      >
        {childArray.map((child, i) => (
          <div
            key={i}
            className="w-full shrink-0 snap-start overflow-y-auto px-4"
          >
            {child}
          </div>
        ))}
      </div>
      {childArray.length > 1 && (
        <div
          className="flex items-center justify-center gap-1.5 pt-3 pb-1"
          role="tablist"
          aria-label="List pages"
        >
          {childArray.map((_, i) => {
            const isActive = i === activeIndex;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Go to list ${i + 1} of ${childArray.length}`}
                onClick={() => scrollToIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-200 ease-out ${
                  isActive
                    ? "w-6 bg-warm-blue"
                    : "w-1.5 bg-warm-border hover:bg-warm-blue-light"
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BoardMobileLayout;
